/** @jsxImportSource @emotion/react */
import type { ReactNode } from 'react'
import css from '@emotion/css/macro'
import { FireOutlined } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import type {
  LastPlayed,
  ThisWeekSummary,
} from '@sportninja/common/features/profile/stats/types'
import { getResultColor } from '@sportninja/common/features/profile/stats/utils/seriesColors'
import { canDrawRadar } from '@sportninja/common/features/profile/stats/utils/thisWeekRadar'
import { capitalizeWords } from '@sportninja/common/ts-utils'

import Text from 'src/components/ts-components/Text'

import ThisWeekRadar from './ThisWeekRadar'

interface ThisWeekStripProps {
  summary: ThisWeekSummary
}

const STREAK_ICON_SIZE = 16
const NO_GAMES_TEXT = 'No games yet this week'

interface NumberBlockProps {
  value: string
  label: string
  color: string
  /** Left-aligned in the radar layout's stat row; centred otherwise. */
  alignStart?: boolean
}

const NumberBlock = ({
  value,
  label,
  color,
  alignStart = false,
}: NumberBlockProps) => {
  'use memo'

  return (
    <div css={[numberBlockStyles, alignStart && alignStartStyles]}>
      <Text variant='heading5' weight='bold' color={color}>
        {value}
      </Text>
      <Text variant='paragraphXSmall' color={colors.NEUTRAL_50}>
        {label}
      </Text>
    </div>
  )
}

interface StreakBadgeProps {
  label: string
  isActive: boolean
}

const StreakBadge = ({ label, isActive }: StreakBadgeProps) => {
  'use memo'

  const color = isActive ? colors.PRIMARY : colors.NEUTRAL_50
  return (
    <div
      css={[badgeStyles, isActive ? activeBadgeStyles : inactiveBadgeStyles]}
    >
      <FireOutlined style={{ color, fontSize: STREAK_ICON_SIZE }} />
      <Text variant='paragraphSmall' weight='semiBold' color={color}>
        {label}
      </Text>
    </div>
  )
}

interface WeekTitleProps {
  dateRange: string
  /** Extra lines under the dates (the radar layout's last-played line). */
  children?: ReactNode
}

const WeekTitle = ({ dateRange, children }: WeekTitleProps) => (
  <div css={titleStyles}>
    <Text variant='heading6' weight='semiBold' color={colors.WHITE}>
      {capitalizeWords('This Week')}
    </Text>
    <Text variant='paragraphSmall' color={colors.NEUTRAL_50}>
      {dateRange}
    </Text>
    {children}
  </div>
)

interface LastPlayedLineProps {
  lastPlayed: LastPlayed
}

const LastPlayedLine = ({ lastPlayed }: LastPlayedLineProps) => (
  <Text variant='paragraphSmall' color={colors.NEUTRAL_50}>
    {lastPlayed.text} ·{' '}
    <span css={resultStyles(getResultColor(lastPlayed.result.outcome, 'dark'))}>
      {lastPlayed.result.label}
    </span>
  </Text>
)

/**
 * Summary of the current week above the Progress card, with the weekly streak.
 * A week with games splits in two: the info box (title, streak, last game, the
 * week's numbers) on the left and, with 3+ count stats, its radar on the right.
 */
const ThisWeekStrip = ({ summary }: ThisWeekStripProps) => {
  'use memo'

  const streakBadge = (
    <StreakBadge label={summary.streakLabel} isActive={summary.hasStreak} />
  )

  if (summary.radar) {
    return (
      <section css={[stripStyles, radarStripStyles]} aria-label='This week'>
        <div css={infoStyles}>
          <div css={infoHeaderStyles}>
            <WeekTitle dateRange={summary.dateRange}>
              {summary.lastPlayed ? (
                <LastPlayedLine lastPlayed={summary.lastPlayed} />
              ) : null}
            </WeekTitle>
            {streakBadge}
          </div>
          <div css={infoStatsStyles}>
            <NumberBlock
              value={String(summary.gamesPlayed)}
              label='Games'
              color={colors.WHITE}
              alignStart
            />
            {summary.radar.map((axis) => (
              <NumberBlock
                key={axis.abbr}
                value={axis.value}
                label={axis.name}
                color={colors.WHITE}
                alignStart
              />
            ))}
          </div>
        </div>
        {canDrawRadar(summary.radar) ? (
          <>
            <div css={dividerStyles} />
            <ThisWeekRadar
              axes={summary.radar}
              gamesPlayed={summary.gamesPlayed}
            />
          </>
        ) : null}
      </section>
    )
  }

  // Without a radar, a week with games is All Sports: just the count.
  const renderBody = () => {
    if (summary.hasGames) {
      return (
        <NumberBlock
          value={String(summary.gamesPlayed)}
          label='Games, all sports'
          color={colors.INFO_300}
        />
      )
    }
    return (
      <div css={noGamesStyles}>
        <Text variant='paragraphSmall' weight='semiBold' color={colors.WHITE}>
          {NO_GAMES_TEXT}
        </Text>
        {summary.lastPlayed ? (
          <LastPlayedLine lastPlayed={summary.lastPlayed} />
        ) : null}
      </div>
    )
  }

  return (
    <section css={stripStyles} aria-label='This week'>
      <WeekTitle dateRange={summary.dateRange} />
      <div css={dividerStyles} />
      {renderBody()}
      <div css={spacerStyles} />
      {streakBadge}
    </section>
  )
}

// ─── Styles ──────────────────────────────────────────────────────

const stripStyles = css`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 24px;
  padding: 16px 24px;
  border: 1px solid ${colors.SECONDARY_300};
  border-radius: 8px;
  background: ${colors.SECONDARY_400};
`

/** The radar sits flush right; the info box takes the rest and wraps above it. */
const radarStripStyles = css`
  align-items: stretch;
`

const infoStyles = css`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: space-between;
  gap: 24px;
  min-width: 280px;
`

const infoHeaderStyles = css`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`

const infoStatsStyles = css`
  display: flex;
  flex-wrap: wrap;
  gap: 16px 32px;
`

const titleStyles = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const dividerStyles = css`
  align-self: stretch;
  width: 1px;
  background: ${colors.SECONDARY_200};
`

const numberBlockStyles = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
`

const alignStartStyles = css`
  align-items: flex-start;
`

/** Win / loss / tie colour, same as the season table's game rows. */
const resultStyles = (color: string) => css`
  color: ${color};
`

const noGamesStyles = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const spacerStyles = css`
  flex: 1;
`

const badgeStyles = css`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 8px;
`

const activeBadgeStyles = css`
  background: ${colors.PRIMARY}24;
`

const inactiveBadgeStyles = css`
  background: ${colors.SECONDARY_300};
`

export default ThisWeekStrip
